import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    Archive,
    Download,
    Eye,
    Loader2,
    RefreshCw,
    Search,
    Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { kusurSorgu } from "@/lib/api";

export default function KusurKayitlarTab({ kayitlar, loading, onRefresh, onPdf, onLoadKayit, onSil }) {
    const [arama, setArama] = useState("");
    const [detay, setDetay] = useState(null);
    const [detayLoading, setDetayLoading] = useState(false);

    const filtered = useMemo(() => {
        const q = arama.trim().toLowerCase();
        if (!q) return kayitlar;
        return kayitlar.filter(
            (k) =>
                (k.baslik || "").toLowerCase().includes(q) ||
                (k.plaka_a || "").toLowerCase().includes(q) ||
                (k.plaka_b || "").toLowerCase().includes(q) ||
                String(k.sigorta_dosyasi_id || "").includes(q),
        );
    }, [kayitlar, arama]);

    const acDetay = async (id) => {
        setDetayLoading(true);
        try {
            const r = await kusurSorgu.detay(id);
            setDetay(r.item || null);
        } catch {
            setDetay(null);
        } finally {
            setDetayLoading(false);
        }
    };

    return (
        <div className="grid lg:grid-cols-[1fr_320px] gap-4 min-h-[400px]">
            <div className="rounded-xl border border-white/10 bg-[#121A2F]/40 overflow-hidden flex flex-col">
                <div className="flex flex-wrap items-center gap-2 p-3 border-b border-white/10">
                    <Archive className="h-4 w-4 text-[#a31d21]" />
                    <span className="text-sm font-medium text-white">Kusur Analiz Kayıtları</span>
                    <span className="text-[10px] text-slate-500 ml-auto">{filtered.length} kayıt</span>
                    <Button size="sm" variant="outline" className="h-7 border-white/10 text-xs" onClick={onRefresh}>
                        <RefreshCw className="h-3 w-3 mr-1" /> Yenile
                    </Button>
                </div>
                <div className="p-3 border-b border-white/5">
                    <div className="relative">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-500" />
                        <Input
                            className="h-8 pl-8 bg-[#0B1220] border-white/10 text-sm"
                            placeholder="Başlık, plaka ara…"
                            value={arama}
                            onChange={(e) => setArama(e.target.value)}
                        />
                    </div>
                </div>
                <div className="flex-1 overflow-auto">
                    {loading ? (
                        <div className="flex items-center gap-2 text-xs text-slate-500 p-6">
                            <Loader2 className="h-4 w-4 animate-spin" /> Yükleniyor…
                        </div>
                    ) : !filtered.length ? (
                        <p className="text-sm text-slate-500 p-6">Kayıt bulunamadı.</p>
                    ) : (
                        <table className="w-full text-sm">
                            <thead className="sticky top-0 bg-[#121A2F]">
                                <tr className="text-left text-slate-500 text-[10px] uppercase border-b border-white/10">
                                    <th className="py-2 px-3">Tarih</th>
                                    <th className="py-2 px-3">Başlık / Dosya</th>
                                    <th className="py-2 px-3">A / B</th>
                                    <th className="py-2 px-3">Güven</th>
                                    <th className="py-2 px-3">İşlem</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((k) => (
                                    <tr
                                        key={k.id}
                                        className={`border-b border-white/5 text-slate-300 hover:bg-white/[0.02] cursor-pointer ${detay?.id === k.id ? "bg-[#a31d21]/10" : ""}`}
                                        onClick={() => acDetay(k.id)}
                                    >
                                        <td className="py-2 px-3 text-[10px] font-mono">
                                            {k.olusturma_tarihi?.slice(0, 10)}
                                        </td>
                                        <td className="py-2 px-3">
                                            <div className="text-xs truncate max-w-[180px]">{k.baslik}</div>
                                            {k.sigorta_dosyasi_id ? (
                                                <Link
                                                    to={`/e/sigorta-dosyalari/${k.sigorta_dosyasi_id}`}
                                                    className="text-[10px] text-[#a31d21] hover:underline"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    Dosya #{k.sigorta_dosyasi_id}
                                                </Link>
                                            ) : null}
                                        </td>
                                        <td className="py-2 px-3 font-mono text-xs">
                                            %{k.oneri_kusur_a ?? "—"} / %{k.oneri_kusur_b ?? "—"}
                                        </td>
                                        <td className="py-2 px-3 text-[10px]">{k.guven_skoru ?? "—"}</td>
                                        <td className="py-2 px-3" onClick={(e) => e.stopPropagation()}>
                                            <div className="flex gap-1">
                                                <button
                                                    type="button"
                                                    className="p-1 text-emerald-400 hover:bg-emerald-500/10 rounded"
                                                    title="PDF"
                                                    onClick={() => onPdf(k.id)}
                                                >
                                                    <Download className="h-3.5 w-3.5" />
                                                </button>
                                                <button
                                                    type="button"
                                                    className="p-1 text-sky-400 hover:bg-sky-500/10 rounded"
                                                    title="Analize yükle"
                                                    onClick={() => onLoadKayit?.(k)}
                                                >
                                                    <Eye className="h-3.5 w-3.5" />
                                                </button>
                                                <button
                                                    type="button"
                                                    className="p-1 text-rose-400 hover:bg-rose-500/10 rounded"
                                                    title="Sil"
                                                    onClick={() => onSil(k.id)}
                                                >
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            <div className="rounded-xl border border-white/10 bg-[#0B1220]/60 p-3 overflow-y-auto max-h-[520px]">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Kayıt Detayı</div>
                {detayLoading ? (
                    <Loader2 className="h-5 w-5 animate-spin text-slate-500" />
                ) : !detay ? (
                    <p className="text-xs text-slate-500">Listeden bir kayıt seçin.</p>
                ) : (
                    <div className="space-y-2 text-[11px]">
                        <div className="text-white font-medium">{detay.baslik}</div>
                        <div className="grid grid-cols-2 gap-2">
                            <div className="rounded border border-white/10 p-2">
                                <div className="text-slate-500">Taraf A</div>
                                <div className="text-emerald-300 font-mono">%{detay.oneri_kusur_a ?? "—"}</div>
                            </div>
                            <div className="rounded border border-white/10 p-2">
                                <div className="text-slate-500">Taraf B</div>
                                <div className="text-rose-300 font-mono">%{detay.oneri_kusur_b ?? "—"}</div>
                            </div>
                        </div>
                        {detay.plaka_a || detay.plaka_b ? (
                            <p className="text-slate-400">
                                {detay.plaka_a} · {detay.plaka_b}
                            </p>
                        ) : null}
                        {detay.guven_skoru != null ? (
                            <p className="text-slate-500">Güven skoru: {detay.guven_skoru}/100</p>
                        ) : null}
                        {detay.sonuc?.ozet ? (
                            <p className="text-slate-300 leading-relaxed border-t border-white/10 pt-2">{detay.sonuc.ozet}</p>
                        ) : null}
                        <div className="flex flex-wrap gap-1 pt-2">
                            <Button size="sm" className="h-7 text-xs bg-[#a31d21]" onClick={() => onLoadKayit?.(detay)}>
                                Analize yükle
                            </Button>
                            <Button size="sm" variant="outline" className="h-7 text-xs border-white/10" onClick={() => onPdf(detay.id)}>
                                PDF
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
